import { icon as svgIcon } from "@eldrun/web-ui/ui";
import { h } from "./dom.ts";
import { KIT_ICON_NODES } from "./kit-icons.ts";
import { barText, clampStep, fraction, pipsText, stepIndex, tabKeyTarget, type KitIcon } from "./kit-model.ts";

/**
 * Kit da UI (F79; F113 no sistema visual, ADR 0024): fábricas de DOM tipadas sobre `h()` (`kit.css`, sem
 * imagem: ícones SVG do Lucide). Sem framework: cada função devolve o elemento pronto; as que guardam valor avisam por
 * `onChange` e têm um `set…` para atualizar no lugar (a UI que muda com o estado não recria nós).
 * Valores dinâmicos vão por `style.setProperty` (a CSP proíbe o atributo `style`).
 */

let nextId = 0;
const uid = (prefix: string): string => `${prefix}-${++nextId}`;

/** Ícone do kit: o SVG do Lucide (F113), decorativo, na cor do texto ou do acento (`currentColor`). */
export function icon(name: KitIcon, extra = ""): SVGSVGElement {
  const el = svgIcon(KIT_ICON_NODES[name], { class: `k-icone ${extra}`.trim() });
  el.setAttribute("data-icone", name);
  return el;
}

/** Divisor de seção: linhas e o ornamento ◆ do mockup. */
export function divider(): HTMLDivElement {
  return h("div", { class: "k-divisor", role: "separator" }, h("span", { class: "k-divisor-ornamento" }));
}

/** Botão quadrado só de ícone (moldura do canto do HUD): fechar, opções, mochila… */
export function iconButton(opts: { icon: KitIcon; label: string; onClick?: () => void }): HTMLButtonElement {
  return h("button", { type: "button", class: "k-botao-icone", "aria-label": opts.label, title: opts.label, onclick: opts.onClick && (() => opts.onClick?.()) }, icon(opts.icon));
}

export interface PanelOptions {
  title: string;
  icon?: KitIcon;
  /** Mostra o ✕ no canto; sem ele o painel não fecha por si. */
  onClose?: () => void;
  /** Classes a mais (`k-painel--sub`: a moldura menor dos blocos dentro de um painel, F82). */
  className?: string;
  /** Rótulo do ✕ (por padrão, "Fechar"). */
  closeLabel?: string;
}

/** Painel com a moldura cristalina em 9 fatias e o ornamento ◆ no topo; os filhos vão no corpo. */
export function panel(opts: PanelOptions, ...children: (Node | string)[]): HTMLElement {
  const titleId = uid("k-titulo");
  return h(
    "section",
    { class: `k-painel ${opts.className ?? ""}`.trim(), "aria-labelledby": titleId },
    h(
      "header",
      { class: "k-painel-topo" },
      opts.icon && icon(opts.icon),
      h("h2", { id: titleId, class: "k-titulo" }, opts.title),
      opts.onClose && iconButton({ icon: "icone-x", label: opts.closeLabel ?? "Fechar", onClick: opts.onClose }),
    ),
    h("div", { class: "k-painel-corpo" }, ...children),
  );
}

export type ButtonVariant = "primario" | "secundario" | "menu" | "acao";

export interface ButtonOptions {
  label: string;
  icon?: KitIcon;
  variant?: ButtonVariant;
  onClick?: () => void;
  disabled?: boolean;
}

/** Botão do kit: `menu` (cristal com ›, ui-a), `primario` (aceso), `secundario` (contorno fino), `acao` (detalhe). */
export function button(opts: ButtonOptions): HTMLButtonElement {
  const variant = opts.variant ?? "secundario";
  return h(
    "button",
    { type: "button", class: `k-botao k-botao--${variant}`, disabled: opts.disabled, onclick: opts.onClick && (() => opts.onClick?.()) },
    opts.icon && icon(opts.icon),
    variant === "menu" && opts.icon && h("span", { class: "k-botao-traco", "aria-hidden": "true" }),
    h("span", { class: "k-rotulo" }, opts.label),
  );
}

export interface TabItem {
  id: string;
  label: string;
  icon?: KitIcon;
}

/**
 * Abas (ui-h): `role="tablist"`, a ativa com `aria-selected` e o foco itinerante (só ela no Tab); setas,
 * Home e End trocam de aba. `select(id)` muda por fora sem chamar `onSelect`.
 */
export function tabs(items: readonly TabItem[], onSelect: (id: string) => void, selected = items[0]?.id): { element: HTMLDivElement; select(id: string): void } {
  const buttons = items.map((item) =>
    h("button", { type: "button", role: "tab", class: "k-aba", id: uid("k-aba"), "data-aba": item.id, onclick: () => choose(item.id, true) }, item.icon && icon(item.icon), h("span", { class: "k-rotulo" }, item.label)),
  );
  const element = h("div", { class: "k-abas", role: "tablist" }, ...buttons);
  const select = (id: string): void => {
    items.forEach((item, i) => {
      const on = item.id === id;
      buttons[i]!.setAttribute("aria-selected", String(on));
      buttons[i]!.tabIndex = on ? 0 : -1;
      buttons[i]!.classList.toggle("ativa", on);
    });
  };
  const choose = (id: string, notify: boolean): void => {
    select(id);
    if (notify) onSelect(id);
  };
  element.addEventListener("keydown", (e) => {
    const index = buttons.indexOf(e.target as HTMLButtonElement);
    const target = index < 0 ? undefined : tabKeyTarget(e.key, index, buttons.length);
    if (target === undefined) return;
    e.preventDefault();
    buttons[target]!.focus();
    choose(items[target]!.id, true);
  });
  if (selected !== undefined) select(selected);
  return { element, select };
}

export interface SlotOptions {
  /** Nome para leitores de tela (o item ou "vazio"). */
  label: string;
  icon?: KitIcon;
  /** Conteúdo próprio (o ícone 3D do item, por exemplo) no lugar do ícone recortado. */
  content?: Node;
  qty?: number;
  /** Nível em pips (◆), como os itens. */
  tier?: number;
  /** Número do atalho (cinto 1–8). */
  key?: string;
  selected?: boolean;
  onClick?: () => void;
}

/** Slot da grade (ui-c); o selecionado com os cantos acesos (ui-d). */
export function slot(opts: SlotOptions): HTMLButtonElement {
  return h(
    "button",
    { type: "button", class: `k-slot${opts.selected ? " ativo" : ""}`, "aria-label": opts.label, "aria-pressed": String(Boolean(opts.selected)), onclick: opts.onClick && (() => opts.onClick?.()) },
    opts.key && h("span", { class: "k-slot-tecla", "aria-hidden": "true" }, opts.key),
    opts.content ?? (opts.icon && icon(opts.icon)),
    opts.tier !== undefined && opts.tier > 0 && h("span", { class: "k-slot-pips", "aria-hidden": "true" }, pipsText(opts.tier, opts.tier).full),
    opts.qty !== undefined && opts.qty > 1 && h("span", { class: "k-slot-qtd", "aria-hidden": "true" }, String(opts.qty)),
  );
}

export interface BarOptions {
  label: string;
  value: number;
  max: number;
  icon?: KitIcon;
  /** Texto à direita; por padrão `valor / máximo`. */
  text?: string;
}

/** Barra com ícone, nome e número (ui-b vitais, ui-c peso): `role="progressbar"`. */
export function bar(opts: BarOptions): HTMLDivElement {
  const el = h(
    "div",
    { class: "k-barra", role: "progressbar", "aria-label": opts.label, "aria-valuemin": 0 },
    opts.icon && icon(opts.icon),
    h("span", { class: "k-barra-nome" }, opts.label),
    h("span", { class: "k-barra-valor" }),
    h("span", { class: "k-barra-trilha" }, h("span", { class: "k-barra-cheio" })),
  );
  setBar(el, opts.value, opts.max, opts.text);
  return el;
}

/** Atualiza uma `bar()` no lugar. */
export function setBar(el: HTMLElement, value: number, max: number, text = barText(value, max)): void {
  el.setAttribute("aria-valuenow", String(Math.round(value)));
  el.setAttribute("aria-valuemax", String(Math.round(max)));
  el.setAttribute("aria-valuetext", text);
  el.style.setProperty("--p", String(fraction(value, 0, max)));
  const label = el.querySelector(".k-barra-valor");
  if (label) label.textContent = text;
}

export interface SelectOption {
  value: string;
  label: string;
}

/** Seletor (ui-h "Fullscreen ▼"): o `<select>` nativo (teclado e leitor de tela de graça) com a moldura. */
export function select(opts: { label: string; options: readonly SelectOption[]; value?: string; onChange?: (value: string) => void }): HTMLSelectElement {
  const el = h("select", { class: "k-seletor", "aria-label": opts.label }, ...opts.options.map((o) => h("option", { value: o.value }, o.label)));
  if (opts.value !== undefined) el.value = opts.value;
  el.addEventListener("change", () => opts.onChange?.(el.value));
  return el;
}

/** Seletor de passos ‹ valor › (ui-h "VSync"): botões anterior/próximo e as setas do teclado. */
export function stepper(opts: { label: string; options: readonly string[]; index?: number; wrap?: boolean; onChange?: (index: number) => void }): HTMLDivElement {
  let index = stepIndex(opts.index ?? 0, 0, opts.options.length);
  const value = h("output", { class: "k-passo-valor", "aria-live": "polite" });
  const prev = h("button", { type: "button", class: "k-passo-seta k-passo-menos", "aria-label": `${opts.label}: anterior` }, icon("icone-chevron"));
  const next = h("button", { type: "button", class: "k-passo-seta k-passo-mais", "aria-label": `${opts.label}: próximo` }, icon("icone-chevron"));
  const el = h("div", { class: "k-passo", role: "group", "aria-label": opts.label }, prev, value, next);
  const render = (): void => {
    value.textContent = opts.options[index] ?? "";
    prev.disabled = !opts.wrap && index <= 0;
    next.disabled = !opts.wrap && index >= opts.options.length - 1;
  };
  const move = (delta: number): void => {
    const to = stepIndex(index, delta, opts.options.length, opts.wrap);
    if (to === index) return;
    index = to;
    render();
    opts.onChange?.(index);
  };
  prev.addEventListener("click", () => move(-1));
  next.addEventListener("click", () => move(1));
  el.addEventListener("keydown", (e) => {
    const delta = e.key === "ArrowLeft" || e.key === "ArrowDown" ? -1 : e.key === "ArrowRight" || e.key === "ArrowUp" ? 1 : 0;
    if (delta === 0) return;
    e.preventDefault();
    move(delta);
  });
  render();
  return el;
}

/** Alternador liga/desliga (ui-h): `role="switch"` com `aria-checked`. */
export function toggle(opts: { label: string; checked?: boolean; onChange?: (checked: boolean) => void }): HTMLButtonElement {
  const el = h("button", { type: "button", role: "switch", class: "k-alternador", "aria-label": opts.label, "aria-checked": String(Boolean(opts.checked)) });
  el.addEventListener("click", () => {
    const on = el.getAttribute("aria-checked") !== "true";
    el.setAttribute("aria-checked", String(on));
    opts.onChange?.(on);
  });
  return el;
}

export interface SliderOptions {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  /** Texto do número ao lado (por padrão o próprio valor). */
  format?: (value: number) => string;
  onChange?: (value: number) => void;
}

/** Controle deslizante (ui-h "Brightness"): `<input type="range">` nativo com a trilha e o ◆ do kit. */
export function slider(opts: SliderOptions): HTMLDivElement {
  const step = opts.step ?? 1;
  const format = opts.format ?? String;
  const input = h("input", { type: "range", class: "k-deslizante-trilha", "aria-label": opts.label, min: opts.min, max: opts.max, step });
  const out = h("output", { class: "k-deslizante-valor", "aria-hidden": "true" });
  const el = h("div", { class: "k-deslizante" }, input, out);
  const render = (value: number): void => {
    input.value = String(value);
    input.setAttribute("aria-valuetext", format(value));
    input.style.setProperty("--p", String(fraction(value, opts.min, opts.max)));
    out.textContent = format(value);
  };
  input.addEventListener("input", () => {
    const value = clampStep(Number(input.value), opts.min, opts.max, step);
    render(value);
    opts.onChange?.(value);
  });
  render(clampStep(opts.value, opts.min, opts.max, step));
  return el;
}

/** Pips ◆◇ (nível, raridade, cargas): `role="img"` com "n de max". */
export function pips(n: number, max: number): HTMLSpanElement {
  const { full, empty } = pipsText(n, max);
  return h("span", { class: "k-pips", role: "img", "aria-label": `${full.length} de ${full.length + empty.length}` }, h("span", { class: "k-pips-cheios" }, full), h("span", { class: "k-pips-vazios" }, empty));
}

/** Seção com título (ui-h "▼ DISPLAY"); `collapsible` usa `<details>` (abre e fecha pelo teclado). */
export function section(title: string, collapsible = false, ...children: (Node | string)[]): HTMLElement {
  if (!collapsible) return h("section", { class: "k-secao" }, h("h3", { class: "k-secao-titulo" }, title), h("div", { class: "k-secao-corpo" }, ...children));
  return h("details", { class: "k-secao", open: true }, h("summary", { class: "k-secao-titulo" }, icon("icone-triangulo", "k-secao-seta"), title), h("div", { class: "k-secao-corpo" }, ...children));
}

/** Linha de atributo (ui-c "WEIGHT 0.0", ui-e "LEVEL 12"): ícone, nome e valor à direita. */
export function statRow(iconName: KitIcon | undefined, label: string, value: string | Node): HTMLDivElement {
  return h("div", { class: "k-linha" }, iconName ? icon(iconName) : h("span", { class: "k-icone vazio", "aria-hidden": "true" }), h("span", { class: "k-linha-nome" }, label), h("span", { class: "k-linha-valor" }, value));
}

/** Selo (ui-c "COMMON", "STACKABLE"). */
export function badge(text: string): HTMLSpanElement {
  return h("span", { class: "k-selo" }, text);
}

/** Linha de opção (ui-h): o nome à esquerda e o controle à direita, ligados por `<label>`/`aria-labelledby`. */
export function field(label: string, control: HTMLElement): HTMLDivElement {
  const id = uid("k-campo");
  const target = control.matches("input, select, button, [role]") ? control : control.querySelector<HTMLElement>("input, select, [role]");
  target?.setAttribute("aria-labelledby", id);
  return h("div", { class: "k-campo" }, h("span", { id, class: "k-campo-nome" }, label), control);
}
